import React, {useEffect, useState} from 'react';
import {AurDropdownButton} from '../aur-button/AurDropdownButton';
import AurIcon, {IconColors, IconNames} from '../aur-icon/AurIcon';
import {t} from 'i18next';
import {
  getBranchList,
  getDepartmentList,
  getPermissionsbyClientId,
} from '../../../api/ClientManagementService';
import {Permission} from '../../../types/permission-group/permission';
import AurLoadingIndicator from '../aur-loading-indicator/AurLoadingIndictor';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {
  CLIENT_PERMISSION_CODES_CONSTANTS,
  ECA_PERMISSION_CODES_CONSTANTS,
  PERMISSION_TYPE,
} from '../../../constants/constants';
import LocaleStrings from '../../../locales/en.json';
import {
  clearDrawerState,
  setBranches,
  setClientDetailsFormState,
  setCreateMode,
  setDrawerAccordionDisable,
  setGroupedDepartments,
} from '../../../redux/slices/clientDetailsSlice';
import {
  getPermission,
  resetPermission,
} from '../../../redux/slices/permissionSlice';
import {setClientId} from '../../../redux/slices/savedSearchesTemplateSlice';
import {useNavigate} from 'react-router-dom';
import {
  Branch,
  GroupedDepartments,
} from '../../../types/client-management/departmentTypes';
import {branchList, departmentList} from '../../../data/clientDetailsDummyData';
import {Department, UserDetail} from '../../../types/types';
import {isEmptyInstance, isValidGuid} from '../../../utils/utils';
import {getUserDetailsByUserIds} from '../../../api/UserManagementService';
import {useModal} from '../../../contexts/modalContext/ModalContext';
import PermissionGroupDrawer from '../../permission-groups/PermissionGroupDrawer';
import ClientAlertDrawer from '../../permission-groups/ClientAlertDrawer';
import DomainWhitelistDrawer from '../../client-details/DomainWhitelistDrawer';
import AurDrawer from '../aur-drawer/AurDrawer';
import AurDrawerPanelContainer from '../../client-details/AurDrawerPanelContainer';
import AurDrawerClientAddDepartment from '../../client-details/AurDrawerClientAddDepartment';
import AurStackedAccordion from '../../client-details/AurStackedAccordion';
import AurAskLioDrawerHeaderButton from '../../aur-ask-lio/AurAskLioDrawerHeaderButton';
import {Box} from '@mui/material';
import AurButton from '../aur-button/AurButton';

type AurManageClientDropdownProps = {
  onUpdateDrawerParent?: () => void;
  showProjectSettings?: boolean;
  clientId?: string;
};

const AurManageClientDropdown: React.FC<AurManageClientDropdownProps> = ({
  onUpdateDrawerParent,
  showProjectSettings = false,
  clientId,
}) => {
  const [manageItem, setManageItem] = useState('');
  const [isPermissionListLoading, setIsPermissionListLoading] =
    useState<boolean>(false);
  const [clientAlertDrawerOpen, setclientAlertDrawerOpen] =
    useState<boolean>(false);
  const [isGetBranchListLoading, setIsGetBranchListLoading] =
    useState<boolean>(false);
  const [clientDomainWhitelistDrawerOpen, setclientDomainWhitelistDrawerOpen] =
    useState<boolean>(false);
  const [drawerToggleState, setDrawerToggleState] = useState<boolean>(false);
  const [isGetDepartmentListLoading, setIsGetDepartmentListLoading] =
    useState<boolean>(false);
  const [permissionGroupOpen, setPermissionGroupOpen] =
    useState<boolean>(false);
  const [
    hasEcaSavedSearchTemplatePermission,
    setHasEcaSavedSearchTemplatePermission,
  ] = useState<boolean>(false);

  const [
    hasManageClientSettingsPermission,
    setHasManageClientSettingsPermission,
  ] = useState<boolean>(false);

  const [
    hasManagePermissionGroupsPermission,
    setHasManagePermissionGroupsPermission,
  ] = useState<boolean>(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {showModal} = useModal();
  const filterValidGuids = (strings: string[]): string[] => {
    return strings.filter(isValidGuid);
  };
  const groupedDepartmentList = useSelector(
    (state: RootState) => state.department.groupedDepartmentList
  );

  const clientDetail = useSelector(
    (state: RootState) => state.clientList.clientDetail
  );

  const formState = useSelector(
    (state: RootState) => state.department.isFormStateChanged
  );
  const groupDepartmentsByBranch = (
    departments: Department[]
  ): GroupedDepartments[] => {
    const grouped = departments.reduce(
      (groupedDepartments: GroupedDepartments[], department: Department) => {
        const {
          clientBranchId,
          clientDepartmentId,
          name,
          description,
          project,
          editedOn,
          editedBy,
          clientBranchName,
        } = department;

        let branch = groupedDepartments.find(
          (item) => item.clientBranchId === clientBranchId
        );

        if (!branch) {
          branch = {
            clientBranchId,
            branchExpanded: true,
            departmentExpanded: true,
            clientBranchName,
            departments: [],
          };
          groupedDepartments.push(branch);
          groupedDepartments.sort((a, b) =>
            a.clientBranchName.localeCompare(b.clientBranchName)
          );
        }

        branch.departments.push({
          clientBranchId,
          clientDepartmentId,
          name,
          description,
          project,
          expanded: true,
          editedOn,
          editedBy,
          clientBranchName,
        });
        branch.departments.sort((a, b) => a.name.localeCompare(b.name));

        return groupedDepartments;
      },
      []
    );
    grouped.forEach((branch) => {
      // Set branch expanded to false if there are multiple branches
      if (grouped.length > 1) {
        branch.branchExpanded = false;
      }

      // Set department expanded to false if there are multiple departments in a branch
      if (branch.departments.length > 8) {
        branch.departments.forEach((department) => {
          department.expanded = false;
        });
      }
    });

    return grouped;
  };
  const {isEditInProgress} = useSelector(getPermission);

  const setupDepartmentList = (departments: Department[]) => {
    dispatch(setGroupedDepartments(groupDepartmentsByBranch(departments)));
    toggleDrawerPanel(true);
  };

  const checkClientPermission = async () => {
    if (isEmptyInstance(clientId)) {
      return;
    }
    setIsPermissionListLoading(true);
    try {
      const response = await getPermissionsbyClientId(clientId);

      const permissions = [
        ...((response?.data?.data?.value?.permissions || []) as Permission[]),
        ...((response?.data?.data?.value?.projectPermissions ||
          []) as Permission[]),
      ];

      const existsEcaAdminPermission = permissions.some(
        (item) =>
          item.type === ECA_PERMISSION_CODES_CONSTANTS.TYPE_ECI &&
          item.permissionCode ===
            ECA_PERMISSION_CODES_CONSTANTS.ECI_SAVED_SEARCH_TEMPLATES
      );

      const existsViewManagePermissionGroups = permissions.some(
        (item) =>
          item.type === PERMISSION_TYPE.SYSTEM &&
          item.permissionCode ===
            CLIENT_PERMISSION_CODES_CONSTANTS.MANAGE_PERMISSION_GROUPS
      );

      const existsManageClientSettingsPermission = permissions.some(
        (item) =>
          item.type === PERMISSION_TYPE.SYSTEM &&
          item.permissionCode === CLIENT_PERMISSION_CODES_CONSTANTS.CLIENTS_VIEW
      );

      setHasEcaSavedSearchTemplatePermission(existsEcaAdminPermission);
      setHasManageClientSettingsPermission(
        existsManageClientSettingsPermission
      );
      setHasManagePermissionGroupsPermission(existsViewManagePermissionGroups);
    } catch (error) {
      console.error(error);
    } finally {
      setIsPermissionListLoading(false);
    }
  };

  const fetchDepartments = async () => {
    if (isEmptyInstance(clientId)) {
      return;
    }
    setIsGetDepartmentListLoading(true);
    try {
      const reponse = await getDepartmentList(clientId);

      const departments: Department[] = reponse.data.data as Department[];
      const userIds: string[] = filterValidGuids([
        ...new Set(departments.map((department) => department.editedBy)),
      ]);

      if (userIds.length > 0) {
        try {
          const useListResponse = await getUserDetailsByUserIds(userIds);

          const users: UserDetail[] = useListResponse.data
            ?.data as UserDetail[];

          const modifiedList = departments.map((department) => {
            // Find the user in userDetails where userId matches department.editedBy
            const matchingUser = users.find(
              (user) =>
                user.userId.toLowerCase() === department.editedBy.toLowerCase()
            );

            // If a matching user is found, update editedBy with their full name
            if (matchingUser) {
              return {
                ...department,
                editedBy: `${matchingUser.firstName} ${matchingUser.lastName} `,
              };
            }
            return department;
          });

          setupDepartmentList(modifiedList);
          return;
        } catch (error) {
          console.error('Error getting userdetails:', error);
        }
      }
      setupDepartmentList(departments);
    } catch (error) {
      toggleDrawerPanel(true);
      dispatch(setGroupedDepartments(groupDepartmentsByBranch(departmentList)));
    } finally {
      setIsGetDepartmentListLoading(false);
    }
  };
  const fetchBranches = async () => {
    if (isEmptyInstance(clientId)) {
      return;
    }
    setIsGetBranchListLoading(true);
    try {
      const response = await getBranchList(clientId);
      dispatch(setBranches(response.data.data as Branch[]));
    } catch (error) {
      //error
      dispatch(setBranches(branchList));
    } finally {
      setIsGetBranchListLoading(false);
    }
  };

  const handleOnCloseDrawer = () => {
    if (formState) {
      showModal({
        title: t('common.clientDetailsUnsavedChangePopup.title'),
        description: t('common.clientDetailsUnsavedChangePopup.description'),
        labelOkButton: t(
          'common.clientDetailsUnsavedChangePopup.okButtonLabel'
        ),
        labelCancelButton: t(
          'common.clientDetailsUnsavedChangePopup.cancelButtonLabel'
        ),
        onOk: modalOkClickHandler,
      });
      return;
    }
    toggleDrawerPanel(false);
    modalOkClickHandler();
  };

  const toggleDrawerPanel = (toggleState: boolean) => {
    setDrawerToggleState(toggleState);
  };

  const modalOkClickHandler = () => {
    toggleDrawerPanel(false);
    setManageItem('');
    dispatch(clearDrawerState());
    dispatch(setClientDetailsFormState(false));
  };

  useEffect(() => {
    !isEmptyInstance(clientId) && checkClientPermission();
  }, [clientId]);

  const redirectToSavedSearchesPage = () => {
    !isEmptyInstance(clientId) && dispatch(setClientId(clientId));
    navigate('/clients/saved-searches-template', {
      state: {from: 'manage-dropdown'},
    });
  };
  const redirectToClientDetailsPage = () => {
    !isEmptyInstance(clientId) && dispatch(setClientId(clientId));
    navigate('/clients/details', {
      state: {from: 'manage-dropdown'},
    });
  };
  const togglePermissionGroupsDrawer = (shouldForceClose = false) => {
    if (isEditInProgress && !shouldForceClose) {
      showModal({
        title: t(
          'clientDetailsPage.projectDrawer.modal.navigatePanelUnsavedChangePopup.title'
        ),
        description: t(
          'clientDetailsPage.projectDrawer.modal.navigatePanelUnsavedChangePopup.description'
        ),
        labelOkButton: t(
          'clientDetailsPage.projectDrawer.modal.navigatePanelUnsavedChangePopup.okButtonLabel'
        ),
        labelCancelButton: t(
          'clientDetailsPage.projectDrawer.modal.navigatePanelUnsavedChangePopup.cancelButtonLabel'
        ),
        cancelButtonTitle: t(
          'clientDetailsPage.projectDrawer.modal.navigatePanelUnsavedChangePopup.cancelButtonLabel'
        ),
        onOk: () => {
          setPermissionGroupOpen(false);
          setManageItem('');
        },
      });
    } else {
      permissionGroupOpen
        ? setPermissionGroupOpen(false)
        : setPermissionGroupOpen(true);
      setManageItem('');
    }
  };

  useEffect(() => {
    switch (manageItem) {
      case LocaleStrings.clientDetailsPage.managedropDown.Departments: {
        fetchBranches();
        fetchDepartments();

        dispatch(setDrawerAccordionDisable(false));
        dispatch(setCreateMode(false));
        break;
      }
      case LocaleStrings.clientDetailsPage.managedropDown.PermissionGroups: {
        // for Permission Groups
        dispatch(resetPermission());
        setPermissionGroupOpen(true);
        break;
      }
      case LocaleStrings.clientDetailsPage.managedropDown.Alerts: {
        setclientAlertDrawerOpen(true);
        break;
      }
      case LocaleStrings.clientDetailsPage.managedropDown.domainWhitelist: {
        // for Domain Whitelis
        setclientDomainWhitelistDrawerOpen(true);

        console.log(
          LocaleStrings.clientDetailsPage.managedropDown.domainWhitelist
        );
        break;
      }
      case LocaleStrings.clientDetailsPage.managedropDown
        .savedSearchesTemplate: {
        // for Saved Searches Template
        redirectToSavedSearchesPage();
        break;
      }
      case LocaleStrings.clientDetailsPage.managedropDown.projectSettings: {
        redirectToClientDetailsPage();
        break;
      }
      default:
        console.log('defaultcase');
    }
  }, [manageItem]);

  const handleSelect = (item: string) => {
    setManageItem(item);
  };

  const onDepartmentCreated = (apiDepartment: {
    clientDepartmentId: string;
    name: string;
    description: string;
    clientBranchId: string;
    clientBranchName: string;
    createdBy?: string;
    editedBy?: string;
    createdOn?: string;
    editedOn?: string;
  }) => {
    const newDepartment: Department = {
      clientDepartmentId: apiDepartment.clientDepartmentId,
      name: apiDepartment.name,
      description: apiDepartment.description ?? '',
      clientBranchId: apiDepartment.clientBranchId,
      clientBranchName: apiDepartment.clientBranchName,
      editedBy: apiDepartment.editedBy || apiDepartment.createdBy || '',
      editedOn: apiDepartment.editedOn || apiDepartment.createdOn || '',
      project: [],
    };
    const currentDepartments = groupedDepartmentList.flatMap((branch) =>
      branch.departments.map((dept) => ({
        clientBranchId: branch.clientBranchId,
        clientDepartmentId: dept.clientDepartmentId,
        name: dept.name,
        description: dept.description,
        project: dept.project,
        editedOn: dept.editedOn,
        editedBy: dept.editedBy,
        clientBranchName: branch.clientBranchName,
      }))
    );
    setupDepartmentList([...currentDepartments, newDepartment]);
  };
  const onUpdateDrawer = () => {
    onUpdateDrawerParent?.();
  };

  const panelHeader = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <AurButton
        label={t('common.close')}
        type="exit"
        classNames={'drawer-close-button'}
        size="Medium"
        title={t('common.close')}
        onClick={handleOnCloseDrawer}
        dataTestId="client-drawer-close"
      />

      <AurAskLioDrawerHeaderButton />
    </Box>
  );

  const panelContainer = (
    <AurDrawerPanelContainer
      panelHeader={panelHeader}
      contentHeader={
        <AurDrawerClientAddDepartment
          onDepartmentCreated={onDepartmentCreated}
          onUpdate={onUpdateDrawer}
        />
      }
      contentBody={<AurStackedAccordion clientId={clientId ?? ''} />}
    />
  );
  return (
    <div>
      {hasManageClientSettingsPermission ||
      hasManagePermissionGroupsPermission ||
      showProjectSettings ||
      hasEcaSavedSearchTemplatePermission ? (
        <AurDropdownButton
          elementID="client-manage-dropdown"
          buttonText={t('fieldManagement.manage')}
          type="continue"
          children={
            <AurIcon
              icon={IconNames.CaretDown}
              color={IconColors.Custom}
              customClassName="client-profile-details-dropdown-icon"
            />
          }
          aria-live="polite"
          size="Medium"
          iconType="Arrow"
          iconSize="medium"
          iconClassName="client-profile-details-dropdown-icon"
          menuDirectionLeft={false}
          menuItemHeight="medium"
          menuItemWidth="medium"
          menuItemCustomWidth="197px"
          menuItemTextSize="small"
          menuItems={[
            ...(hasManageClientSettingsPermission
              ? [
                  {
                    label: t('clientDetailsPage.managedropDown.Departments'),
                    onClick: () => {
                      handleSelect(
                        t('clientDetailsPage.managedropDown.Departments')
                      );
                    },
                  },
                ]
              : []),
            ...(hasManagePermissionGroupsPermission
              ? [
                  {
                    label: t(
                      'clientDetailsPage.managedropDown.PermissionGroups'
                    ),
                    onClick: () => {
                      handleSelect(
                        t('clientDetailsPage.managedropDown.PermissionGroups')
                      );
                    },
                  },
                ]
              : []),
            ...(hasManageClientSettingsPermission
              ? [
                  {
                    label: t('clientDetailsPage.managedropDown.Alerts'),
                    onClick: () => {
                      handleSelect(
                        t('clientDetailsPage.managedropDown.Alerts')
                      );
                    },
                  },
                ]
              : []),
            ...(hasEcaSavedSearchTemplatePermission
              ? [
                  {
                    label: t(
                      'clientDetailsPage.managedropDown.savedSearchesTemplate'
                    ),
                    onClick: () => {
                      handleSelect(
                        t(
                          'clientDetailsPage.managedropDown.savedSearchesTemplate'
                        )
                      );
                    },
                  },
                ]
              : []),
            ...(hasManageClientSettingsPermission
              ? [
                  {
                    label: t(
                      'clientDetailsPage.managedropDown.domainWhitelist'
                    ),
                    onClick: () => {
                      handleSelect(
                        t('clientDetailsPage.managedropDown.domainWhitelist')
                      );
                    },
                  },
                ]
              : []),
            ...(showProjectSettings
              ? [
                  {
                    label: t(
                      'clientDetailsPage.managedropDown.projectSettings'
                    ),
                    onClick: () => {
                      handleSelect(
                        t('clientDetailsPage.managedropDown.projectSettings')
                      );
                    },
                  },
                ]
              : []),
          ]}
        />
      ) : null}
      <AurDrawer
        className="client-drawer"
        open={drawerToggleState}
        onClose={() => handleOnCloseDrawer()}
        anchor="right"
        drawerContent={panelContainer}
      />
      {permissionGroupOpen && clientId && (
        <PermissionGroupDrawer
          shouldShow={permissionGroupOpen}
          onClose={togglePermissionGroupsDrawer}
          clientId={clientId}
          clientName={clientDetail?.clientName}
          budgetConsumptionEnabled={!!clientDetail?.budgetConsumptionEnabled}
          onUpdate={onUpdateDrawer}
          showAddGrpOnOpen={false}
        />
      )}
      {!isEmptyInstance(clientId) && (
        <div data-testid="client-details">
          <ClientAlertDrawer
            shouldShow={clientAlertDrawerOpen}
            clientId={clientId}
            clientName={clientDetail.clientName}
            setclientAlertDrawerOpen={setclientAlertDrawerOpen}
            setManageItem={setManageItem}
          />
          <DomainWhitelistDrawer
            shouldShow={clientDomainWhitelistDrawerOpen}
            clientId={clientId}
            clientName={clientDetail.clientName}
            setDomainWhitelistDrawerOpen={setclientDomainWhitelistDrawerOpen}
            setManageItem={setManageItem}
          />
        </div>
      )}
      {
        <AurLoadingIndicator
          overlay={true}
          isOpen={
            isPermissionListLoading ||
            isGetBranchListLoading ||
            isGetDepartmentListLoading
          }
        />
      }
    </div>
  );
};

export default AurManageClientDropdown;
